import axios from 'axios';
import { getAuthHeaders } from '../../../utils/authHeaders';
import { 
  Configuration, 
  ConfigurationCategory, 
  CreateConfigurationRequest, 
  UpdateConfigurationRequest, 
  BulkCreateConfigurationRequest,
  ConfigurationResponse,
  ConfigurationCategoryType 
} from '../types';

const API_BASE_URL = '/api/configuration';

class ConfigurationService {
  private getAuthHeaders() {
    return getAuthHeaders();
  }
  /**
   * Get all configurations grouped by category
   */
  async getAllConfigurations(includeInactive = false): Promise<ConfigurationResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}`, {
        params: { includeInactive },
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching all configurations:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch configurations',
        error: error.message
      };
    }
  }

  /**
   * Get configurations by category
   */
  async getConfigurationsByCategory(
    category: ConfigurationCategoryType, 
    includeInactive = false
  ): Promise<ConfigurationResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}/category/${category}`, {
        params: { includeInactive },
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching configurations by category:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch configurations',
        error: error.message
      };
    }
  }

  /**
   * Get a specific configuration
   */
  async getConfiguration(
    category: ConfigurationCategoryType, 
    key: string
  ): Promise<ConfigurationResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}/${category}/${key}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching configuration:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch configuration',
        error: error.message
      };
    }
  }

  /**
   * Create a new configuration
   */
  async createConfiguration(configData: CreateConfigurationRequest): Promise<ConfigurationResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}`, configData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error creating configuration:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create configuration',
        error: error.message
      };
    }
  }

  /**
   * Update a configuration
   */
  async updateConfiguration(
    category: ConfigurationCategoryType,
    key: string,
    updateData: UpdateConfigurationRequest
  ): Promise<ConfigurationResponse> {
    try {
      const response = await axios.put(`${API_BASE_URL}/${category}/${key}`, updateData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating configuration:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update configuration',
        error: error.message
      };
    }
  }

  /**
   * Update a configuration with product sync (for product categories)
   */
  async updateConfigurationWithSync(
    category: ConfigurationCategoryType,
    key: string,
    updateData: UpdateConfigurationRequest
  ): Promise<ConfigurationResponse> {
    try {
      const response = await axios.put(`${API_BASE_URL}/${category}/${key}/sync`, updateData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating configuration with sync:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update configuration',
        error: error.message
      };
    }
  }

  /**
   * Delete a configuration
   */
  async deleteConfiguration(
    category: ConfigurationCategoryType,
    key: string
  ): Promise<ConfigurationResponse> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/${category}/${key}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error deleting configuration:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete configuration',
        error: error.message
      };
    }
  }

  /**
   * Delete a configuration with product sync (for product categories)
   */
  async deleteConfigurationWithSync(
    category: ConfigurationCategoryType,
    key: string
  ): Promise<ConfigurationResponse> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/${category}/${key}/sync`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error deleting configuration with sync:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete configuration',
        error: error.message
      };
    }
  }

  /**
   * Toggle configuration status
   */
  async toggleConfigurationStatus(
    category: ConfigurationCategoryType,
    key: string
  ): Promise<ConfigurationResponse> {
    try {
      const response = await axios.patch(`${API_BASE_URL}/${category}/${key}/toggle`, {}, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error toggling configuration status:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to toggle configuration status',
        error: error.message
      };
    }
  }

  /**
   * Bulk create configurations
   */
  async bulkCreateConfigurations(
    configurations: CreateConfigurationRequest[]
  ): Promise<ConfigurationResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/bulk`, { configurations }, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error bulk creating configurations:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to bulk create configurations',
        error: error.message
      };
    }
  }

  /**
   * Get configuration categories
   */
  async getConfigurationCategories(): Promise<ConfigurationResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}/categories`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching configuration categories:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch configuration categories',
        error: error.message
      };
    }
  }

  /**
   * Get configurations for dropdown/select components
   */
  async getConfigurationsForSelect(
    category: ConfigurationCategoryType,
    includeInactive = false
  ): Promise<{ value: any; label: string }[]> {
    try {
      const result = await this.getConfigurationsByCategory(category, includeInactive);
      if (result.success && Array.isArray(result.data)) {
        return (result.data as Configuration[]).map(config => ({
          value: config.value,
          label: config.label
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching configurations for select:', error);
      return [];
    }
  }
}

export default new ConfigurationService();
